<script setup lang="ts">
import { DATA_TEST_IDS } from "@/constants/data-test-ids/data-test-ids.constant";
import { EProgramApplianceCategory } from "@/domains/v4b/program/program.types";
import { useTypedIntl } from "@/locales/typed-intl";
import { cn } from "@/utils/libs/tailwind-utils";

interface IProps {
  applianceCategories: EProgramApplianceCategory[];
  activeApplianceCategory: EProgramApplianceCategory;
  creationMode: boolean;
  disabled?: boolean;
  validatedApplianceCategories: EProgramApplianceCategory[];
  isEditing?: boolean;
}

interface IEmits {
  (e: "update:activeApplianceCategory", category: EProgramApplianceCategory): void;
}

const props = withDefaults(defineProps<IProps>(), {
  disabled: false,
});
const emit = defineEmits<IEmits>();
const { t, k } = useTypedIntl();

function isCategoryDisabled(category: EProgramApplianceCategory): boolean {
  if (props.disabled) return true;

  const categoryIndex = props.applianceCategories.indexOf(category);
  const firstUnvalidatedIndex = props.applianceCategories.findIndex(
    (applianceCategory) => !props.validatedApplianceCategories.includes(applianceCategory),
  );

  return props.isEditing && firstUnvalidatedIndex >= 0 && categoryIndex > firstUnvalidatedIndex;
}

function isCategoryChecked(category: EProgramApplianceCategory): boolean {
  return props.validatedApplianceCategories.includes(category);
}
</script>

<template>
  <section class="rounded-xl border border-neutral-light bg-white p-6">
    <div class="flex h-10 gap-3 overflow-x-auto">
      <button
        v-for="category in props.applianceCategories"
        v-tooltip.top="
          isCategoryDisabled(category)
            ? t(k.components.programApplianceStepperCard.disabledApplianceTooltip)
            : undefined
        "
        :key="category"
        :aria-current="props.isEditing && category === props.activeApplianceCategory ? 'step' : undefined"
        :class="
          cn(
            'flex h-10 shrink-0 items-center justify-center gap-2 overflow-hidden rounded-full border bg-white px-4 py-2 text-sm font-semibold text-text-primary transition-colors',
            {
              'border-[#609cf1]': category === props.activeApplianceCategory,
              'border-[#e8e8e8]': category !== props.activeApplianceCategory,
              'hover:border-brand/50': category !== props.activeApplianceCategory && !isCategoryDisabled(category),
              'opacity-70': isCategoryDisabled(category),
            },
          )
        "
        :data-test-id="DATA_TEST_IDS.components.programEditor.applianceStep"
        :data-appliance-category="category"
        :disabled="isCategoryDisabled(category)"
        type="button"
        @click="emit('update:activeApplianceCategory', category)"
      >
        <span
          v-if="props.isEditing"
          :class="
            cn('flex size-[14px] shrink-0 items-center justify-center rounded-full border', {
              'border-brand bg-brand text-surface-inverse': isCategoryChecked(category),
              'border-[#f0f0f4] bg-white': !isCategoryChecked(category),
            })
          "
          :data-test-id="DATA_TEST_IDS.components.programEditor.applianceStatus"
          :data-appliance-category="category"
        >
          <i
            v-if="isCategoryChecked(category)"
            class="pi pi-check !text-[10px]"
          />
        </span>
        <span>{{ t(k.global.applianceCategory[category]) }}</span>
      </button>
    </div>

    <div class="min-w-0 pt-3">
      <slot />
    </div>
  </section>
</template>
